import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { successStories } from '@/mocks/stories';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';
import SEOHead from '@/components/feature/SEOHead';

function StoryCard({ story, index }: { story: typeof successStories[0]; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className="bg-white rounded-2xl overflow-hidden transition-all duration-700"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
        transitionDelay: `${index * 80}ms`,
      }}
    >
      {/* Wedding Photo */}
      <div className="relative w-full h-56 overflow-hidden">
        <img
          src={story.weddingPhoto}
          alt={story.couple}
          className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        <div className="absolute bottom-4 left-4 text-white">
          <h3 className="text-xl font-bold" style={{ fontFamily: "'Noto Serif SC', serif" }}>{story.couple}</h3>
          <div className="flex items-center gap-2 text-white/80 text-xs mt-1">
            <i className="ri-map-pin-line"></i>{story.location}
            <span>·</span>
            <i className="ri-calendar-2-line"></i>{story.marriedDate} 结婚
          </div>
        </div>
        <div className="absolute top-3 right-3 bg-red-600 text-white text-xs px-3 py-1 rounded-full font-semibold">
          成功牵手
        </div>
      </div>

      <div className="p-5">
        {/* Before photos */}
        <div className="flex gap-3 mb-4">
          <div className="flex items-center gap-2 flex-1 bg-gray-50 rounded-xl p-2">
            <img src={story.beforePhoto1} alt="husband" className="w-10 h-10 rounded-full object-cover object-top flex-shrink-0" />
            <div>
              <div className="text-xs font-semibold text-gray-700">{story.husbandJob}</div>
              <div className="text-[10px] text-gray-400">{story.husbandEdu}</div>
            </div>
          </div>
          <div className="w-8 h-8 flex items-center justify-center bg-red-50 rounded-full flex-shrink-0 self-center">
            <i className="ri-heart-fill text-red-500 text-sm"></i>
          </div>
          <div className="flex items-center gap-2 flex-1 bg-gray-50 rounded-xl p-2">
            <img src={story.beforePhoto2} alt="wife" className="w-10 h-10 rounded-full object-cover object-top flex-shrink-0" />
            <div>
              <div className="text-xs font-semibold text-gray-700">{story.wifeJob}</div>
              <div className="text-[10px] text-gray-400">{story.wifeEdu}</div>
            </div>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {story.tags.map((tag, i) => (
            <span key={i} className="text-[10px] px-2 py-0.5 bg-rose-50 text-rose-600 rounded-full border border-rose-100">{tag}</span>
          ))}
        </div>

        {/* Story text */}
        <p className={`text-sm text-gray-600 leading-relaxed ${expanded ? '' : 'line-clamp-3'}`}>
          {story.story}
        </p>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-red-500 mt-1 cursor-pointer hover:text-red-600"
        >
          {expanded ? '收起' : '阅读全文'}
        </button>

        {/* Stats */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-50">
          <div className="flex items-center gap-1">
            {[...Array(story.rating)].map((_, i) => (
              <i key={i} className="ri-star-fill text-yellow-400 text-sm"></i>
            ))}
          </div>
          <div className="text-xs text-gray-400">
            <i className="ri-time-line mr-1"></i>相识 {story.duration} 后结婚
          </div>
        </div>
      </div>
    </div>
  );
}

const STORIES_JSONLD = [
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '富缘佳旺成功牵手故事 - 真实婚恋成功案例',
    description: '富缘佳旺会员真实成功故事，12万+成功牵手，98%真实认证率，平均13个月从相识到结婚。',
    url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/stories`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: import.meta.env.VITE_SITE_URL || 'https://example.com' },
        { '@type': 'ListItem', position: 2, name: '成功故事', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/stories` },
      ],
    },
  },
];

export default function StoriesPage() {
  const navigate = useNavigate();
  const headerRef = useRef<HTMLDivElement>(null);
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation();
  const { ref: gridRef, visibleItems } = useStaggerAnimation(successStories.length);

  return (
    <div className="min-h-screen bg-gray-50 pb-16 md:pb-0">
      <SEOHead
        title="成功牵手故事 - 富缘佳旺真实婚恋案例 | FortuneMatch"
        description="富缘佳旺12万+成功牵手故事，98%真实认证率，平均13个月从相识到结婚。查看真实会员的幸福婚恋故事，开启您的缘分之旅。"
        keywords="婚恋成功故事,富缘佳旺成功案例,视频交友成功,华人婚恋故事"
        canonical="/stories"
        jsonLd={STORIES_JSONLD}
      />
      <Navbar />

      {/* Hero */}
      <section
        className="relative pt-20 pb-16 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #7b1a1a 0%, #c0392b 50%, #e8a87c 100%)' }}
      >
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/2c7412f06aad655fcbb658a59f833969.jpg"
            alt="success stories background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/40"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 text-center py-16">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/70 hover:text-white text-sm mb-8 mx-auto cursor-pointer transition-colors"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </button>
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-5">
            <i className="ri-heart-fill text-red-400 text-sm"></i>
            <span className="text-xs text-white/90">真实故事 · 真实幸福</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Noto Serif SC', serif" }}>
            成功牵手故事
          </h1>
          <p className="text-white/70 text-base max-w-xl mx-auto">
            每一段爱情都始于真实的相遇。这里记录着富缘佳旺会员们的幸福故事，他们因教育、因真诚、因缘分走到了一起。
          </p>
        </div>
      </section>

      {/* Stats Bar */}
      <section
        ref={statsRef as React.RefObject<HTMLElement>}
        className="bg-white border-b border-gray-100 py-8"
      >
        <div className="max-w-5xl mx-auto px-4 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { num: '12万+', label: '成功牵手', icon: 'ri-heart-2-line' },
            { num: '98%', label: '真实认证率', icon: 'ri-shield-check-line' },
            { num: '平均13个月', label: '从相识到结婚', icon: 'ri-time-line' },
            { num: '4.9/5', label: '用户满意度', icon: 'ri-star-line' },
          ].map((s, i) => (
            <div
              key={i}
              className="text-center transition-all duration-700"
              style={{
                opacity: statsVisible ? 1 : 0,
                transform: statsVisible ? 'translateY(0)' : 'translateY(20px)',
                transitionDelay: `${i * 100}ms`,
              }}
            >
              <div className="w-10 h-10 flex items-center justify-center bg-red-50 rounded-xl mx-auto mb-2">
                <i className={`${s.icon} text-red-500`}></i>
              </div>
              <div className="text-xl font-bold text-gray-800">{s.num}</div>
              <div className="text-xs text-gray-400 mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Stories Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2" style={{ fontFamily: "'Noto Serif SC', serif" }}>
              他们的幸福，就是我们的骄傲
            </h2>
            <p className="text-gray-400 text-sm">所有故事均为真实会员分享，已获授权发布</p>
          </div>

          <div
            ref={gridRef as React.RefObject<HTMLDivElement>}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {successStories.map((story, i) => (
              <div
                key={story.id}
                className="transition-all duration-700"
                style={{
                  opacity: visibleItems[i] ? 1 : 0,
                  transform: visibleItems[i] ? 'translateY(0)' : 'translateY(40px)',
                }}
              >
                <StoryCard story={story} index={i} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-red-600 to-rose-600">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center text-white">
          <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ fontFamily: "'Noto Serif SC', serif" }}>
            您的故事，即将开始
          </h2>
          <p className="text-white/80 text-sm mb-8">加入600万+认证会员，开启您的幸福缘分之旅</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/')}
              className="px-8 py-3 bg-white text-red-600 rounded-full font-semibold text-sm hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
            >
              立即注册 <i className="ri-arrow-right-line ml-1"></i>
            </button>
            <button
              onClick={() => navigate('/')}
              className="px-8 py-3 border-2 border-white text-white rounded-full font-semibold text-sm hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
            >
              浏览精选会员
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
